import React, { useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ContentManager = () => {
  const { siteContent, updateContent } = useAdmin();
  const [editingSection, setEditingSection] = useState(null);
  const [editingData, setEditingData] = useState({});

  const sections = [
    {
      id: 'hero',
      title: 'Hero Section',
      fields: [
        { key: 'title', label: 'Main Title', type: 'text' },
        { key: 'subtitle', label: 'Subtitle', type: 'textarea' },
        { key: 'cta', label: 'CTA Button Text', type: 'text' },
      ]
    },
    {
      id: 'about',
      title: 'About Section',
      fields: [
        { key: 'title', label: 'Section Title', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'mission', label: 'Mission Statement', type: 'textarea' },
        { key: 'vision', label: 'Vision Statement', type: 'textarea' },
      ]
    },
    {
      id: 'services',
      title: 'Services Section',
      fields: [
        { key: 'title', label: 'Section Title', type: 'text' },
        { key: 'subtitle', label: 'Section Subtitle', type: 'textarea' },
      ]
    },
    {
      id: 'contact',
      title: 'Contact Information',
      fields: [
        { key: 'address', label: 'Address', type: 'textarea' },
        { key: 'phone', label: 'Phone', type: 'text' },
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'hours', label: 'Business Hours', type: 'text' },
      ]
    },
    {
      id: 'footer',
      title: 'Footer Content',
      fields: [
        { key: 'companyName', label: 'Company Name', type: 'text' },
        { key: 'copyright', label: 'Copyright Text', type: 'text' },
      ]
    }
  ];

  const startEditing = (sectionId) => {
    setEditingSection(sectionId);
    setEditingData(siteContent[sectionId] || {});
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditingData({});
  };

  const saveChanges = (sectionId) => {
    Object.keys(editingData).forEach(key => {
      updateContent(sectionId, key, editingData[key]);
    });
    setEditingSection(null);
    setEditingData({});
  };

  const handleFieldChange = (key, value) => {
    setEditingData(prev => ({ ...prev, [key]: value }));
  };

  const renderField = (field, value) => {
    const commonClasses = "w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text";

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className={commonClasses}
            rows={3}
          />
        );
      case 'email':
      case 'text':
      default:
        return (
          <input
            type={field.type}
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className={commonClasses}
          />
        );
    }
  };

  return (
    <div className="section-padding">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-naqd-light-text dark:text-naqd-dark-text mb-2">
              Content Manager
            </h1>
            <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
              Edit your website's text content across all sections
            </p>
          </div>

          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section.id} className="card">
                <h3 className="text-xl font-semibold mb-4 text-naqd-light-text dark:text-naqd-dark-text">
                  {section.title}
                </h3>
                
                {editingSection === section.id ? (
                  <div className="space-y-4">
                    {section.fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                          {field.label}
                        </label>
                        {renderField(field, editingData[field.key])}
                      </div>
                    ))}
                    <div className="flex space-x-3">
                      <button
                        onClick={() => saveChanges(section.id)}
                        className="px-4 py-2 bg-naqd-primary text-white rounded-lg hover:bg-naqd-primary/90 transition-colors flex items-center"
                      >
                        <CheckIcon className="w-4 h-4 mr-2" />
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="px-4 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg hover:bg-naqd-light-surface dark:hover:bg-naqd-dark-surface transition-colors flex items-center"
                      >
                        <XMarkIcon className="w-4 h-4 mr-2" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {section.fields.map((field) => (
                      <div key={field.key} className="flex justify-between items-start"
                      >
                        <div className="flex-1 mr-4">
                          <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary"
                          >
                            {field.label}
                          </label>
                          <p className="text-naqd-light-text dark:text-naqd-dark-text mt-1"
                          >
                            {siteContent[section.id]?.[field.key] || 'Not set'}
                          </p>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => startEditing(section.id)}
                      className="px-4 py-2 bg-naqd-primary text-white rounded-lg hover:bg-naqd-primary/90 transition-colors"
                    >
                      Edit Content
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentManager;